<template>
  <div class="tm-jam-modal" @click.self="$emit('close')">
    <div class="tm-jam-modal__content">
      <span class="close-btn" @click="$emit('close')">
        <i class="icon icon--close-btn size-16"></i>
      </span>
      <div class="tm-jam-modal__header">
        {{ $t('reporting.timeMachine.jamPayment.title') }}
      </div>
      <div class="tm-jam-modal__reservation" v-if="reservationData">
        <div class="reservation-title">{{ $t('reporting.timeMachine.jamPayment.reservationInfo') }}</div>
        <div class="reservation-row">
          <span class="reservation-label">{{ $t('reporting.timeMachine.jamPayment.startDate') }}</span>
          <span class="reservation-value">{{ formattedDate }}</span>
        </div>
        <div class="reservation-row">
          <span class="reservation-label">{{ $t('reporting.timeMachine.jamPayment.startTime') }}</span>
          <span class="reservation-value">{{ formattedTime }}</span>
        </div>
        <div class="reservation-row">
          <span class="reservation-label">{{ $t('reporting.timeMachine.jamPayment.durationLabel') }}</span>
          <span class="reservation-value">{{ $t('reporting.timeMachine.jamPayment.durationValue', { value: reservationData.duration }) }}</span>
        </div>
      </div>
      <div class="tm-jam-modal__body">
        <div class="jam-section-title">{{ $t('reporting.timeMachine.jamPayment.jamTitle') }}</div>
        <template v-if="remainJam > 0">
          <div class="detail-info-wrapper jam">
            <div class="detail-info-item has-jam">
              <div class="title">{{ $t('common.holding') }}</div>
              <div class="value">{{ remainJam | thousand }}</div>
            </div>
            <div class="detail-info-item use-jam">
              <div class="title">{{ $t('common.spending') }}</div>
              <div class="value">- {{ useJam | thousand }}</div>
            </div>
            <div class="detail-info-item leftover-jam">
              <div class="title">{{ $t('common.remaining') }}</div>
              <div class="value" v-if="leftoverJam >= 0">
                {{ leftoverJam | thousand }}
                <i class="icon icon--jam-bottle"></i>
              </div>
              <div class="error-msg" v-else>{{ $t('common.jamCharging') }}</div>
            </div>
          </div>
          <div class="changing-jam-btn">
            <button class="btf-button icon outline" @click="$emit('charge-jam', remainJam)">
              <i class="icon icon--plus"></i>
              {{ $t('common.chargeJam') }}
            </button>
          </div>
        </template>
        <template v-else>
          <div class="detail-info-wrapper no-jam">
            <div class="no-jam-contents">
              <div class="text" v-html="$t('common.noJam')"></div>
              <div class="changing-jam-btn">
                <button class="btf-button icon outline" @click="$emit('charge-jam', remainJam)">
                  <i class="icon icon--plus"></i>
                  {{ $t('common.chargeJam') }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="tm-jam-modal__notice">
        <ul class="notice-list">
          <li class="notice-item"
              v-for="(rule, idx) in 4" :key="idx"
              v-html="$t(`reporting.timeMachine.jamPayment.rules[${idx}]`)">
          </li>
        </ul>
      </div>
      <div class="tm-jam-modal__footer">
        <div class="tm-jam-modal__check-confirm">
          <ui-checkbox :value="agreed" @input="agreed = $event" :disabled="disableAgreement">
            {{ $t('aiReport.paymentConfirm') }}
          </ui-checkbox>
        </div>
        <div class="payment-button-wrapper">
          <button class="btf-button back" @click="$emit('back')">
            <i class="icon icon--inflow__back"></i>
            {{ $t('common.prev') }}
          </button>
          <button class="btf-button primary confirm" :class="{ disabled: !agreed || disableAgreement }" @click="onConfirm">
            {{ $t('reporting.timeMachine.popup.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getRemainJam } from '@/api/jam'
import UiCheckbox from '@/components/ui/UiCheckbox'

export default {
  name: 'TimeMachineJamPaymentModal',
  components: {
    UiCheckbox
  },
  props: {
    jamCost: {
      type: Number,
      default: 10
    },
    reservationData: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      remainJam: 0,
      agreed: false
    }
  },
  computed: {
    useJam () {
      return this.jamCost
    },
    leftoverJam () {
      return this.remainJam - this.useJam
    },
    disableAgreement () {
      return this.leftoverJam < 0
    },
    formattedDate () {
      if (!this.reservationData || !this.reservationData.startDate) return ''
      const d = this.reservationData.startDate
      if (d instanceof Date) {
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}-${m}-${day}`
      }
      return String(d).slice(0, 10)
    },
    formattedTime () {
      if (!this.reservationData) return ''
      const h = String(this.reservationData.hour).padStart(2, '0')
      const m = String(this.reservationData.minute).padStart(2, '0')
      return `${h}:${m}`
    }
  },
  mounted () {
    this.fetchJamInfo()
  },
  methods: {
    fetchJamInfo () {
      getRemainJam(null, true).then(res => {
        this.remainJam = res.paidCount + res.freeCount
      }).catch(() => {
        this.remainJam = 0
      })
    },
    onConfirm () {
      if (!this.agreed || this.disableAgreement) return
      this.$emit('confirm')
    }
  }
}
</script>

<style scoped lang="scss">
.tm-jam-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;

  * {
    box-sizing: border-box;
  }

  &__content {
    position: relative;
    width: 530px;
    background: #FFF;
    border-radius: 14px;
    padding: 30px 30px 25px 30px;
    display: flex;
    flex-direction: column;

    .close-btn {
      position: absolute;
      top: 15px;
      right: 15px;
      cursor: pointer;
    }
  }

  &__header {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    color: #2F2F2F;
  }

  &__reservation {
    margin-top: 24px;
    background-color: #F4F4F4;
    border-radius: 5px;
    border: 1px solid #D7D7D7;
    padding: 20px 30px;

    .reservation-title {
      font-size: 14px;
      font-weight: 600;
      color: #2F2F2F;
      margin-bottom: 16px;
    }

    .reservation-row {
      display: flex;
      align-items: center;
      padding: 6px 0;

      .reservation-label {
        width: 80px;
        color: #666;
        font-size: 13px;
        font-weight: 400;
      }

      .reservation-value {
        color: #2F2F2F;
        font-size: 13px;
        font-weight: 500;
      }
    }
  }

  &__body {
    padding: 24px 0 26px 0;

    .jam-section-title {
      font-size: 14px;
      font-weight: 600;
      color: #2F2F2F;
      margin-bottom: 12px;
    }

    .detail-info-wrapper {
      font-size: 13px;
      background-color: #F4F4F4;
      border-radius: 5px;
      border: 1px solid #D7D7D7;
      padding: 14px 30px;

      &.jam {
        & > .detail-info-item {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          font-weight: 500;
          padding: 14px 0;

          &.leftover-jam {
            border-top: 1px dashed #D7D7D7;

            .value, .error-msg {
              color: $brandColor;
            }

            .error-msg {
              font-size: 13px;
              font-weight: 500;
            }
          }

          &.use-jam {
            margin-bottom: 10px;
          }

          &.has-jam {
            .value {
              color: #FC6A00;
            }
          }

          .value {
            font-size: 16px;
          }
        }
      }

      &.no-jam {
        background-color: #f5f5f5;
        border-radius: 4px;
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px;

        .text {
          font-size: 13px;
          line-height: 1.5;
          font-weight: 500;
          color: #393939;
        }

        .changing-jam-btn {
          justify-content: center;
        }
      }
    }

    .changing-jam-btn {
      margin-top: 10px;
      display: flex;
      justify-content: flex-end;
    }
  }

  &__notice {
    border-top: 1px dashed #D7D7D7;
    padding-left: 20px;

    .notice-list {
      color: #666;
      font-size: 13px;
      list-style-type: square;
      margin: 20px 0;

      .notice-item {
        margin-bottom: 8px;
        font-size: 13px;
        line-height: normal;
        letter-spacing: -.5px;

        /deep/ em {
          color: #EC0047;
          font-style: normal;
          font-weight: 600;
        }
      }
    }
  }

  &__footer {
    position: relative;
  }

  &__check-confirm {
    min-height: 44px;
    padding: 12px 0 28px;
    text-align: center;

    /deep/ .ui_checkbox {
      margin-left: 0;

      .text {
        font-size: 13px;
      }
    }
  }

  .payment-button-wrapper {
    display: flex;
    align-items: center;
    padding-top: 24px;
    font-size: 13px;

    .btf-button {
      &.back {
        position: absolute;
        min-width: auto;
        height: auto;
        background: none;
        border: none;
        color: #666;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        padding: 0;

        &:hover {
          color: #444;
        }
      }

      &.confirm {
        margin: 0 auto;
      }
    }
  }
}
</style>
